/**
 * ☰ › Books — every place trades live, and the way in for a broker CSV.
 * Mock 1 in `docs/mockups/position-sizing.html`.
 *
 * A **Book** is where a trade is kept: one per import, plus the app's own paper
 * account. The same list will carry the Analyzer's mix checkboxes once phase 3
 * exists; until then there is nothing to analyze, so neither the checkboxes nor
 * the Analyze button are drawn rather than drawn dead.
 *
 * Only an imported book can be renamed away or deleted from here. Deleting the
 * paper account would take the journal's plans with it, and the server refuses.
 */

import { useCallback, useEffect, useState } from "react";
import { deleteBook, listBooks, renameBook, type Book } from "../api/sizing";
import { ImportWizard } from "./ImportWizard";

/** Dates in the list are shown as the Pacific calendar day, the app's reporting zone. */
const DAY = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" });
const day = (iso?: string) => (iso ? DAY.format(new Date(iso)) : "");

export function BooksPanel() {
  const [books, setBooks] = useState<Book[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState<{ bookId?: string } | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; stage: 1 | 2 } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setBooks(await listBooks());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function act(fn: () => Promise<unknown>) {
    try {
      setError(null);
      await fn();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (importing) {
    return (
      <ImportWizard
        books={(books ?? []).filter((b) => b.kind === "import")}
        initialBookId={importing.bookId}
        onCancel={() => setImporting(null)}
        onDone={() => {
          setImporting(null);
          void refresh();
        }}
      />
    );
  }

  return (
    <div style={PANEL}>
      <div style={BAR}>
        <span style={{ color: "var(--qf-text-secondary)", fontSize: 12 }}>
          Where your trades live. Each import is its own book.
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" style={PRIMARY} onClick={() => setImporting({})}>
          ＋ Import CSV
        </button>
      </div>

      {error && <div style={ERROR}>{error}</div>}
      {books === null && !error && <div style={MUTED}>Loading…</div>}
      {books && books.length === 0 && (
        <div style={MUTED}>No books yet. Import a broker CSV to make the first one.</div>
      )}

      {books && books.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH}>Book</th>
                <th style={TH}>Kind</th>
                <th style={TH}>Broker</th>
                <th style={{ ...TH, textAlign: "right" }}>Trades</th>
                <th style={{ ...TH, textAlign: "right" }}>Open</th>
                <th style={TH}>Date range</th>
                <th style={TH}>Last import</th>
                <th style={{ ...TH, textAlign: "right" }}>Plans</th>
                <th style={TH} />
              </tr>
            </thead>
            <tbody>
              {books.map((b) => (
                <tr key={b.id}>
                  <td style={TD}>
                    {renaming?.id === b.id ? (
                      <input
                        autoFocus
                        style={INPUT}
                        value={renaming.name}
                        onChange={(e) => setRenaming({ id: b.id, name: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const name = renaming.name;
                            setRenaming(null);
                            void act(() => renameBook(b.id, name));
                          } else if (e.key === "Escape") setRenaming(null);
                        }}
                        onBlur={() => setRenaming(null)}
                      />
                    ) : (
                      <>
                        {b.name}
                        {b.kind === "paper" && <span style={{ color: "var(--qf-text-muted)" }}> (simulated)</span>}
                      </>
                    )}
                  </td>
                  <td style={TD}>{b.kind}</td>
                  <td style={TD}>{b.kind === "import" ? b.broker : "—"}</td>
                  <td style={NUM}>{b.trades}</td>
                  <td style={NUM}>{b.open}</td>
                  <td style={TD}>{b.firstFill ? `${day(b.firstFill)} → ${day(b.lastFill)}` : "—"}</td>
                  <td style={TD}>
                    {b.lastImport ? `${day(b.lastImport)} · ${b.imports} file${b.imports === 1 ? "" : "s"}` : "—"}
                  </td>
                  <td style={NUM}>{b.plans}</td>
                  <td style={{ ...TD, position: "relative" }}>
                    {b.kind === "import" && (
                      <button
                        type="button"
                        style={GHOST}
                        aria-label={`Actions for ${b.name}`}
                        onClick={() => setMenuFor(menuFor === b.id ? null : b.id)}
                      >
                        ⋯
                      </button>
                    )}
                    {menuFor === b.id && (
                      <div style={MENU} onMouseLeave={() => setMenuFor(null)}>
                        <button
                          type="button"
                          style={ITEM}
                          onClick={() => {
                            setMenuFor(null);
                            setRenaming({ id: b.id, name: b.name });
                          }}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          style={ITEM}
                          onClick={() => {
                            setMenuFor(null);
                            setImporting({ bookId: b.id });
                          }}
                        >
                          Add file to this book
                        </button>
                        <button
                          type="button"
                          style={{ ...ITEM, color: "var(--qf-negative)" }}
                          onClick={() => {
                            setMenuFor(null);
                            setConfirmDelete({ id: b.id, stage: 1 });
                          }}
                        >
                          Delete book…
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDelete && books && (
        <DeleteConfirm
          book={books.find((b) => b.id === confirmDelete.id)!}
          stage={confirmDelete.stage}
          onCancel={() => setConfirmDelete(null)}
          onNext={() => setConfirmDelete({ ...confirmDelete, stage: 2 })}
          onDelete={() => {
            const id = confirmDelete.id;
            setConfirmDelete(null);
            void act(() => deleteBook(id));
          }}
        />
      )}
    </div>
  );
}

/** Asked twice: a book's fills and its raw files are gone for good. */
function DeleteConfirm({
  book,
  stage,
  onCancel,
  onNext,
  onDelete,
}: {
  book: Book;
  stage: 1 | 2;
  onCancel: () => void;
  onNext: () => void;
  onDelete: () => void;
}) {
  return (
    <div style={{ ...ERROR, display: "flex", flexDirection: "column", gap: 6 }}>
      {stage === 1 ? (
        <>
          Delete <b>{book.name}</b>? Its {book.fills.toLocaleString()} fills, {book.imports} imported file
          {book.imports === 1 ? "" : "s"} and every trade built from them go with it.
        </>
      ) : (
        <>This cannot be undone. Delete {book.name} now?</>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" style={BTN} onClick={onCancel}>
          Keep it
        </button>
        <button
          type="button"
          style={{ ...BTN, borderColor: "var(--qf-negative)", color: "var(--qf-negative)" }}
          onClick={stage === 1 ? onNext : onDelete}
        >
          {stage === 1 ? "Delete…" : "Delete for good"}
        </button>
      </div>
    </div>
  );
}

const PANEL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  width: 760,
  maxWidth: "94vw",
  maxHeight: "72vh",
  overflowY: "auto",
  padding: 14,
  color: "var(--qf-text)",
  font: "13px system-ui, sans-serif",
};
const BAR: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const INPUT: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  color: "var(--qf-text)",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "2px 6px",
};
const BTN: React.CSSProperties = { ...INPUT, padding: "4px 10px", cursor: "pointer" };
const PRIMARY: React.CSSProperties = {
  ...BTN,
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};
const GHOST: React.CSSProperties = { ...BTN, background: "none", border: 0, padding: "0 6px" };
const MENU: React.CSSProperties = {
  position: "absolute",
  right: 0,
  top: "100%",
  zIndex: 5,
  display: "flex",
  flexDirection: "column",
  minWidth: 170,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  padding: 3,
  boxShadow: "0 6px 18px rgba(0,0,0,.35)",
};
const ITEM: React.CSSProperties = {
  ...INPUT,
  border: 0,
  background: "none",
  textAlign: "left",
  padding: "5px 8px",
  cursor: "pointer",
};
const MUTED: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const ERROR: React.CSSProperties = {
  border: "1px solid var(--qf-negative)",
  color: "var(--qf-negative)",
  borderRadius: 5,
  padding: "7px 10px",
  fontSize: 12,
};
const TABLE: React.CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12 };
const TH: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  color: "var(--qf-text-muted)",
  padding: "5px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};
const TD: React.CSSProperties = {
  padding: "5px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
